import { AlertTriangle, Search, X } from 'lucide-react'
import { CsCarteiraTable } from '@/components/Cs/CsCarteiraTable'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useCsCarteiraPage } from '@/hooks/useCsCarteiraPage'
import { CS_STAGE_LABELS, type CsCarteiraStage } from '@/services/csService'
import { cn } from '@/utils/cn'

const STAGES = Object.keys(CS_STAGE_LABELS) as CsCarteiraStage[]

export default function CsCarteiraPage() {
  const page = useCsCarteiraPage()
  const hasActiveFilter = Boolean(page.search.trim() || page.stage || page.riscoOnly)

  return (
    <DashboardLayout title='Sucesso do Cliente — Carteira'>
      <div className='space-y-4'>
        <header>
          <h2 className='text-xl font-semibold text-text-primary'>Carteira de clientes</h2>
          <p className='mt-2 max-w-3xl text-sm leading-relaxed text-text-muted'>
            Clientes ativos pós-venda — histórico, saúde e relacionamento (valores comerciais
            ocultos).
          </p>
        </header>

        <div className='glass-card p-4'>
          <div className='grid w-full grid-cols-1 items-center gap-3 lg:grid-cols-[minmax(0,1fr)_auto_auto]'>
            <div className='relative min-w-0 overflow-hidden rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)]'>
              <Search
                size={16}
                className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-muted'
              />
              <input
                value={page.search}
                onChange={(e) => page.setSearch(e.target.value)}
                placeholder='Buscar empresa ou projeto…'
                className='min-h-[44px] w-full border-0 bg-transparent py-3 pl-10 pr-10 text-sm font-medium text-text-primary placeholder:text-text-secondary outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/25'
              />
              {page.search && (
                <button
                  type='button'
                  onClick={() => page.setSearch('')}
                  className='absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-text-muted hover:text-text-primary'
                  aria-label='Limpar busca'
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <select
              value={page.stage}
              onChange={(e) => page.setStage(e.target.value as CsCarteiraStage | '')}
              className='min-h-[44px] rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 text-sm text-text-primary outline-none focus:border-accent/40'
            >
              <option value=''>Todos os estágios</option>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {CS_STAGE_LABELS[s]}
                </option>
              ))}
            </select>

            <label
              className={cn(
                'flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border px-4 text-sm transition-colors',
                page.riscoOnly
                  ? 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                  : 'border-[var(--glass-border)] bg-[var(--glass-bg)] text-text-muted hover:text-text-primary',
              )}
            >
              <input
                type='checkbox'
                className='sr-only'
                checked={page.riscoOnly}
                onChange={(e) => page.setRiscoOnly(e.target.checked)}
              />
              <AlertTriangle size={14} />
              Risco churn
            </label>
          </div>

          {hasActiveFilter && (
            <div className='mt-3 flex justify-end'>
              <button
                type='button'
                onClick={() => {
                  page.setSearch('')
                  page.setStage('')
                  page.setRiscoOnly(false)
                }}
                className='inline-flex items-center gap-1 text-xs font-medium text-text-muted hover:text-text-primary'
              >
                <X size={12} />
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        <CsCarteiraTable accounts={page.accounts} loading={page.loading} />
      </div>
    </DashboardLayout>
  )
}
